import { useEffect, useState } from 'react';

export const useSelectVaultsCollaborators = (
  vaults: VaultCollaboratorsType[],
) => {
  const [vaultsSelection, setVaultsSelection] = useState<
    {
      id: number;
      selected: boolean | null;
      collaboratorsSelected: VaultCollaboratorType[];
    }[]
  >(
    vaults.map((vault) => ({
      id: vault.id,
      selected: false,
      collaboratorsSelected: [],
    })),
  );

  useEffect(() => {
    const newVaultSelection = vaults.map((vaultList) => {
      const vaultSelection = vaultsSelection.find(
        (vault) => vault.id === vaultList.id,
      );
      const collaboratorsSelected =
        vaultsSelection.find((vault) => vault.id === vaultList.id)
          ?.collaboratorsSelected ?? [];
      const hasSelected =
        (vaultSelection && vaultSelection.selected) ||
        vaultSelection?.selected === null;
      const selected = vaultSelection?.selected === null ? null : hasSelected;
      return {
        id: vaultList.id,
        selected,
        collaboratorsSelected,
      };
    });
    setVaultsSelection(newVaultSelection);
  }, [vaults]);

  const amountSelected = vaultsSelection.reduce(
    (acc, vault) => acc + vault.collaboratorsSelected.length,
    0,
  );

  const updateVaultSelection = (
    vaultId: number,
    selected: boolean | null,
    collaboratorsSelected: VaultCollaboratorType[],
  ) => {
    setVaultsSelection((prevState) =>
      prevState.map((vault) => {
        if (vault.id === vaultId) {
          return {
            ...vault,
            selected,
            collaboratorsSelected,
          };
        }
        return vault;
      }),
    );
  };

  const onSelectCollaborator = (
    vaultId: number,
    collaborator: VaultCollaboratorType,
  ) => {
    const collaboratorsSelected =
      vaultsSelection.find((vault) => vault.id === vaultId)
        ?.collaboratorsSelected ?? [];
    const collaboratorIndex = collaboratorsSelected.findIndex(
      (c) => c.itemId === collaborator.itemId,
    );
    if (collaboratorIndex !== -1) {
      collaboratorsSelected.splice(collaboratorIndex, 1);
    } else {
      collaboratorsSelected.push(collaborator);
    }

    const currentVault = vaults.find((vault) => vaultId === vault.id);

    if (
      currentVault &&
      collaboratorsSelected.length === currentVault.collaborators.length
    ) {
      updateVaultSelection(vaultId, true, collaboratorsSelected);
    } else if (collaboratorsSelected.length > 0) {
      updateVaultSelection(vaultId, null, collaboratorsSelected);
    } else {
      updateVaultSelection(vaultId, false, collaboratorsSelected);
    }
  };

  const onSelectVault = (vaultId: number) => {
    const vaultCollaborators =
      vaults.find((vault) => vaultId === vault.id)?.collaborators ?? [];
    const vaultSelection = vaultsSelection.find(
      (vault) => vault.id === vaultId,
    );
    if (
      vaultSelection &&
      (vaultSelection.selected || vaultSelection.selected === null)
    ) {
      updateVaultSelection(vaultId, false, []);
    } else {
      updateVaultSelection(vaultId, true, vaultCollaborators);
    }
  };

  const onSelectAll = () => {
    if (
      vaultsSelection.filter((vault) => vault.selected).length === vaults.length
    ) {
      vaults.forEach((vault) => {
        updateVaultSelection(vault.id, false, []);
      });
    } else {
      vaults.forEach((vault) => {
        updateVaultSelection(vault.id, true, vault.collaborators);
      });
    }
  };

  const clearSelection = () =>
    vaults.forEach((vault) => {
      updateVaultSelection(vault.id, false, []);
    });

  return {
    onSelectCollaborator,
    onSelectVault,
    vaultsSelection,
    onSelectAll,
    clearSelection,
    amountSelected,
  };
};
